import { useMemo } from "react";

/**
 * A participant is "locked" — read-only — once their lifecycle status moves out
 * of `active`. Editing of any participant-scoped data (profile, study
 * enrolment, appointments, notes, tags, groups, consent, medication,
 * responses, documents) must be blocked while locked. The backend enforces a
 * 403 on these writes; the FE disables the controls so users never hit it.
 *
 * The lock status is read from the same field the participant detail views use
 * (`redactionStatus`), falling back to a generic `status` field for callers
 * that only have the lighter list-row shape.
 */
export const LOCKED_REDACTION_STATUSES = [
  "deactivated",
  "redacted",
  "deleted",
] as const;

export type LockedRedactionStatus =
  (typeof LOCKED_REDACTION_STATUSES)[number];

/** Shared, user-facing message for every locked-edit surface. */
export const PARTICIPANT_LOCKED_MESSAGE =
  "This participant is locked (deactivated/redacted) and cannot be edited.";

/**
 * Returns true when the given participant-like object is locked.
 * Accepts the raw participant (reads `redactionStatus`) or a plain status
 * string. Tolerates `undefined`/`null` (returns false) so callers can pass a
 * still-loading participant without guarding first.
 */
export const isParticipantLocked = (
  participant?: { redactionStatus?: string | null; status?: string | null } | string | null,
): boolean => {
  if (!participant) return false;
  const status =
    typeof participant === "string"
      ? participant
      : participant.redactionStatus ?? participant.status ?? "";
  return LOCKED_REDACTION_STATUSES.includes(status as LockedRedactionStatus);
};

/**
 * Hook form of {@link isParticipantLocked}. Memoised so the boolean identity is
 * stable across renders unless the underlying status changes.
 */
export const useParticipantLock = (
  participant?: { redactionStatus?: string | null; status?: string | null } | string | null,
): boolean => {
  const status =
    participant == null
      ? null
      : typeof participant === "string"
      ? participant
      : participant.redactionStatus ?? participant.status ?? null;
  return useMemo(() => isParticipantLocked(status), [status]);
};

/**
 * Study Add Tag / Add Note. Matches RBAC EDIT_TAGS / EDIT_NOTES:
 * locked, archived, excluded, withdrawn, closed study (`dataEditable`),
 * plus the redaction lock when that field is present.
 */
export const canMutateStudyParticipantTagsNotes = ({
  hasEditPermission,
  dataEditable,
  isLocked,
  isArchived,
  isExcluded,
  isWithdrawn,
  redactionStatus,
}: {
  hasEditPermission: boolean;
  dataEditable: boolean;
  isLocked?: boolean | null;
  isArchived?: boolean | null;
  isExcluded?: boolean | null;
  isWithdrawn?: boolean | null;
  redactionStatus?: string | null;
}): boolean =>
  !!hasEditPermission &&
  !!dataEditable &&
  !isLocked &&
  !isArchived &&
  !isExcluded &&
  !isWithdrawn &&
  !isParticipantLocked(redactionStatus);
